import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { UpdateMyNetworkClientRequest } from '@1po/1po-bff-fe-spec/generated/my_network/request/UpdateMyNetworkClientRequest';
import { Form as AntForm, Form as AntdForm } from 'antd';
import { ROUTER_MY_NETWORK } from 'app/AppRouter';
import Loader from 'components/Loader';
import {
  getMyNetworkClientsIsSubmitting,
  getSelectedClient,
  updateMyNetworkClientsRequestSaga,
} from 'domains/r1Network/R1Network.store';
import { DELIVERY_METHODS, FORM_FIELDS } from 'pages/MyNetworkPage/GarageDetails/GarageDetails.constants';
import { ClientSettingsFormType } from 'pages/MyNetworkPage/GarageDetails/GarageDetails.types';
import { haveFormValuesChanged } from 'pages/MyNetworkPage/GarageDetails/GarageDetails.utils';
import { GarageDetailsContact } from 'pages/MyNetworkPage/GarageDetails/GarageDetailsContact';
import { GarageDetailsForm } from 'pages/MyNetworkPage/GarageDetails/GarageDetailsForm';
import { GarageDetailsHeader } from 'pages/MyNetworkPage/GarageDetails/GarageDetailsHeader';
import { BlackButton, Flex, Text } from 'UI';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import { Container } from '../MyNetworkPage.styled';

export const GarageDetailsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const garageDetails = useSelector(getSelectedClient);
  const isSubmitting = useSelector(getMyNetworkClientsIsSubmitting);

  const [form] = AntdForm.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const values = AntForm.useWatch([], form);
  const isActive = !!values?.[FORM_FIELDS.ACTIVE];

  useResetScroll();

  const handleSave = useCallback(
    (formValues: ClientSettingsFormType) => {
      if (!garageDetails) return;

      const request: UpdateMyNetworkClientRequest = {
        uniqueId: garageDetails.uniqueId,
        accountCommercialLinksId: garageDetails.accountCommercialLinksId,
        active: formValues.active,
        dmsAccountInfoId: garageDetails.dmsAccountInfoId,
        deliveryMethod: formValues.deliveryMethod || DELIVERY_METHODS.DIRECT,
        automaticOrderTransfer: formValues.automaticOrderTransfer,
        manageCreditLimit: formValues.manageCreditLimit,
        maxOrderAmount: formValues.maxOrderAmount ?? '0',
        maxInvoiceAmount: formValues.maxInvoiceAmount ?? '0',
        actualInvoiceAmount: garageDetails.actualInvoiceAmount ?? '0',
      };

      setHasFormChanged(false);
      dispatch(updateMyNetworkClientsRequestSaga(request));
    },
    [dispatch, garageDetails],
  );

  const handleActiveChange = useCallback(
    (val: boolean) => {
      if (!val) {
        form.setFieldsValue({
          [FORM_FIELDS.ACTIVE]: false,
          [FORM_FIELDS.DELIVERY_METHOD]: DELIVERY_METHODS.DIRECT,
          [FORM_FIELDS.AUTOMATIC_ORDER_TRANSFER]: false,
          [FORM_FIELDS.MANAGE_CREDIT_LIMIT]: false,
          [FORM_FIELDS.MAX_ORDER_AMOUNT]: undefined,
          [FORM_FIELDS.MAX_INVOICE_AMOUNT]: undefined,
        });
      }
    },
    [form],
  );

  useEffect(() => {
    form
      .validateFields()
      .then(() => {
        setIsFormValid(true);
      })
      .catch((error_) => {
        if (error_.errorFields?.length > 0) {
          setIsFormValid(false);
        }
      });
  }, [form, values]);

  const handleValuesChange = useCallback(
    (_changedValues: Partial<ClientSettingsFormType>, allValues: ClientSettingsFormType) => {
      if (!garageDetails) {
        return;
      }
      const changed = haveFormValuesChanged(garageDetails, allValues);
      setHasFormChanged(changed);
    },
    [garageDetails],
  );

  useEffect(() => {
    if (garageDetails) {
      form.resetFields();
    } else {
      history.push(ROUTER_MY_NETWORK);
    }
  }, [garageDetails, history, form]);

  if (!garageDetails) {
    return <></>;
  }

  return (
    <Container>
      <Flex direction={'column'} gap={25}>
        <Flex>
          <Text type={'h0'} data-cy={'my-network-garage-details-title'}>
            {t('my_network.garage_details.title', 'Garage Details')}
          </Text>
        </Flex>
        <GarageDetailsHeader form={form} garageDetails={garageDetails} onActiveChange={handleActiveChange} />
        <Flex direction={'row'} gap={25} align={'flex-start'}>
          <Flex direction={'column'} size={3}>
            <GarageDetailsForm
              form={form}
              onFinish={handleSave}
              onValuesChange={handleValuesChange}
              garageDetails={garageDetails}
              disabled={!isActive || isSubmitting}
            />
          </Flex>
          <Flex direction={'column'} gap={20} size={2}>
            <GarageDetailsContact garageDetails={garageDetails} />
            <Flex minWidth={150} alignSelf={'flex-end'} direction={'column'}>
              <BlackButton
                onClick={() => form.submit()}
                htmlType={'button'}
                disabled={!isFormValid || !hasFormChanged || isSubmitting}
                size={'xlarge'}
              >
                {isSubmitting ? <Loader size={'small'} height={'5px'} /> : t('common.action.save', 'Save')}
              </BlackButton>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Container>
  );
};
